import axios from "axios";
import { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import EarnixLogo from "../components/EarnixLogo";
import useIsMobile from "../hooks/useIsMobile";
import { statusToast } from "../utils/notify";

import API from "../config.js";
const G   = "#FFB800";
const G2  = "#FFD700";
const GD  = "#C8860A";
const GRAD = `linear-gradient(135deg, ${G2}, ${G}, ${GD})`;

export default function Profile() {
  const [user,    setUser]    = useState(null);
  const [form,    setForm]    = useState({ name: "", phone: "" });
  const [status,  setStatusRaw]  = useState(null);
  const setStatus = (s) => { setStatusRaw(s); statusToast(s); };
  const [loading, setLoading] = useState(false);
  const [copied,  setCopied]  = useState(false);
  const [pageLoad,setPageLoad]= useState(true);
  const isMobile = useIsMobile();

  // Password change state
  const [pwForm,    setPwForm]    = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [pwStatus,  setPwStatus]  = useState(null);
  const [pwLoading, setPwLoading] = useState(false);
  const [showPwForm,setShowPwForm]= useState(false);

  const token   = localStorage.getItem("token");
  const headers = { Authorization: "Bearer " + token };

  useEffect(() => {
    if (!token) { window.location.href = "/"; return; }
    axios.get(`${API}/api/dashboard`, { headers })
      .then(res => {
        const d   = res.data;
        const inv = d.investment;
        setUser({
          name:         d.name,
          email:        d.email,
          balance:      d.balance,
          totalDeposit: d.totalDeposit,
          totalWithdraw:d.totalWithdraw,
          totalEarning: d.totalEarning,
          referralCode: d.referralCode,
          plan:         d.plan,
          joinDate:     d.joinDate,
          planName:     inv?.planName || null,
        });
        setForm({ name: d.name || "", phone: "" });
        // Also fetch phone from profile
        axios.get(`${API}/api/profile`, { headers })
          .then(p => setForm(f => ({ ...f, phone: p.data.phone || "" })))
          .catch(() => {});
      })
      .catch(err => {
        if (err.response?.status === 401) { localStorage.removeItem("token"); window.location.href = "/"; }
      })
      .finally(() => setPageLoad(false));
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) return setStatus({ type: "error", msg: "Name cannot be empty." });
    setLoading(true); setStatus(null);
    try {
      const res = await axios.put(`${API}/api/profile`, form, { headers });
      setStatus({ type: "success", msg: res.data.message });
      setUser(prev => ({ ...prev, ...form }));
    } catch (e) {
      setStatus({ type: "error", msg: e.response?.data?.message || "Update failed." });
    } finally { setLoading(false); }
  };

  const handlePasswordChange = async () => {
    const { currentPassword, newPassword, confirmPassword } = pwForm;
    if (!currentPassword || !newPassword || !confirmPassword)
      return setPwStatus({ type: "error", msg: "All password fields are required." });
    if (newPassword !== confirmPassword)
      return setPwStatus({ type: "error", msg: "New passwords do not match." });
    if (newPassword.length < 6)
      return setPwStatus({ type: "error", msg: "Password must be at least 6 characters." });

    setPwLoading(true); setPwStatus(null);
    try {
      const res = await axios.put(`${API}/api/change-password`, { currentPassword, newPassword }, { headers });
      if (res.data.message?.toLowerCase().includes("incorrect") || res.data.message?.toLowerCase().includes("required")) {
        setPwStatus({ type: "error", msg: res.data.message });
      } else {
        setPwStatus({ type: "success", msg: res.data.message || "Password changed!" });
        setPwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setTimeout(() => setShowPwForm(false), 2000);
      }
    } catch (e) {
      setPwStatus({ type: "error", msg: e.response?.data?.message || "Failed to change password." });
    } finally { setPwLoading(false); }
  };

  const refLink = `${window.location.origin}/login?ref=${user?.referralCode || ""}`;
  const copyRef = () => {
    navigator.clipboard.writeText(refLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (pageLoad) return (
    <div style={s.page}>
      <Navbar />
      <div style={{ display:"flex", justifyContent:"center", alignItems:"center", height:"70vh" }}>
        <div style={{ animation:"logoPulse 1.5s ease-in-out infinite" }}><EarnixLogo size={70} glow /></div>
      </div>
    </div>
  );

  const initials = (user?.name || "U").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
  const joinDate = user?.joinDate ? new Date(user.joinDate).toLocaleDateString("en-IN", { day:"numeric", month:"long", year:"numeric" }) : "—";

  return (
    <div style={s.page}>
      <Navbar />
      <div style={{ ...s.container, padding: isMobile ? "20px 14px" : "32px 24px" }} className="page-container">

        {/* Header */}
        <div style={s.header}>
          <h1 style={s.title}>My Profile</h1>
          <p style={s.sub}>Manage your account information</p>
        </div>

        <div style={{ ...s.grid, gridTemplateColumns: isMobile ? "1fr" : "320px 1fr" }} className="profile-grid">

          {/* ── Left: Avatar + Stats ── */}
          <div style={s.leftCol}>

            {/* Avatar card */}
            <div style={s.avatarCard}>
              <div style={s.topLine} />
              <div style={s.avatarWrap}>
                <div style={s.avatarRing}>
                  <div style={s.avatar}>{initials}</div>
                </div>
                <div style={s.avatarGlow} />
              </div>
              <div style={s.uName}>{user?.name || "—"}</div>
              <div style={s.uEmail}>{user?.email || "—"}</div>
              <div style={{ ...s.planBadge, background: GRAD }}>
                {(user?.planName || user?.plan || "Starter").toUpperCase()} PLAN
              </div>
              <div style={s.joinInfo}>Member since {joinDate}</div>
            </div>

            {/* Stats */}
            <div style={s.statsCard}>
              <div style={s.topLine} />
              {[
                { lbl: "Wallet Balance",  val: fmtU(user?.balance),       color: G    },
                { lbl: "Total Deposited", val: fmtU(user?.totalDeposit),  color: "#00e87a" },
                { lbl: "Total Withdrawn", val: fmtU(user?.totalWithdraw), color: "#ff6b6b" },
                { lbl: "Total Earned",    val: fmtU(user?.totalEarning),  color: G2   },
              ].map((st, i) => (
                <div key={i} style={{ ...s.statRow, borderBottom: i < 3 ? "1px solid rgba(255,184,0,0.06)" : "none" }}>
                  <span style={s.statLbl}>{st.lbl}</span>
                  <span style={{ ...s.statVal, color: st.color }}>{st.val}</span>
                </div>
              ))}
            </div>

            {/* Referral */}
            <div style={s.refCard}>
              <div style={s.topLine} />
              <div style={s.refHead}>
                <span style={s.refIcon}>⬡</span>
                <span style={s.refTitle}>Referral Link</span>
              </div>
              <div style={s.refLinkBox}>
                <span style={s.refLinkText}>{refLink}</span>
              </div>
              <button style={{ ...s.copyBtn, background: copied ? "#00e87a22" : "rgba(255,184,0,0.08)", borderColor: copied ? "#00e87a44" : "rgba(255,184,0,0.2)", color: copied ? "#00e87a" : G }}
                onClick={copyRef}>
                {copied ? "✓ Copied!" : "📋 Copy Link"}
              </button>
              {/* Share buttons */}
              <div style={s.shareRow}>
                <a href={`https://wa.me/?text=${encodeURIComponent("Join Earnix Global and earn daily USDT! " + refLink)}`}
                  target="_blank" rel="noreferrer"
                  style={{ ...s.shareBtn, background: "#25D366" + "18", color: "#25D366", borderColor: "#25D366" + "30" }}>
                  WhatsApp
                </a>
                <a href={`https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent("Join Earnix Global!")}`}
                  target="_blank" rel="noreferrer"
                  style={{ ...s.shareBtn, background: "#229ED9" + "18", color: "#229ED9", borderColor: "#229ED9" + "30" }}>
                  Telegram
                </a>
                <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Earning daily USDT with Earnix Global! Join me: " + refLink)}`}
                  target="_blank" rel="noreferrer"
                  style={{ ...s.shareBtn, background: "#1DA1F2" + "18", color: "#1DA1F2", borderColor: "#1DA1F2" + "30" }}>
                  Twitter
                </a>
              </div>
            </div>
          </div>

          {/* ── Right: Edit Form ── */}
          <div style={s.rightCol}>
            <div style={s.formCard}>
              <div style={s.topLine} />
              <div style={s.formInner}>
                <div style={s.formTitle}>Edit Profile</div>

                {status && (
                  <div style={{
                    ...s.alert,
                    background: status.type === "success" ? "rgba(0,232,122,0.07)" : "rgba(255,60,87,0.08)",
                    border:     status.type === "success" ? "1px solid rgba(0,232,122,0.25)" : "1px solid rgba(255,60,87,0.3)",
                    color:      status.type === "success" ? "#00e87a" : "#ff6b8a",
                  }}>
                    {status.type === "success" ? "✓" : "⚠"} {status.msg}
                  </div>
                )}

                <Field label="Full Name" value={form.name}
                  onChange={v => setForm(f => ({ ...f, name: v }))}
                  placeholder="Your full name" />

                <Field label="Phone Number" value={form.phone}
                  onChange={v => setForm(f => ({ ...f, phone: v }))}
                  placeholder="+91 XXXXXXXXXX" type="tel" />

                <Field label="Email Address" value={user?.email || ""} readOnly
                  hint="Email cannot be changed" />

                <Field label="Referral Code" value={user?.referralCode || ""} readOnly
                  hint="Your unique referral code" />

                <button
                  style={{ ...s.saveBtn, opacity: loading ? 0.6 : 1, cursor: loading ? "not-allowed" : "pointer" }}
                  onClick={handleSave} disabled={loading}>
                  {loading ? <span style={s.spinner} /> : "💾 Save Changes"}
                </button>

                {/* Security section */}
                <div style={s.secTitle}>Account Security</div>
                <div style={s.secGrid}>
                  {[
                    { icon: "📧", label: "Email Verified", val: "✓ Verified", note: "Linked to your account", color: "#00e87a" },
                    { icon: "📅", label: "Joined", val: joinDate, note: "Account creation date" },
                    { icon: "🔒", label: "Account Status", val: user?.isBlocked ? "⚠ Blocked" : "✓ Active", note: "", color: user?.isBlocked ? "#ff6b6b" : "#00e87a" },
                  ].map((item, i) => (
                    <div key={i} style={s.secItem}>
                      <span style={s.secIcon}>{item.icon}</span>
                      <div>
                        <div style={s.secLbl}>{item.label}</div>
                        <div style={{ ...s.secVal, color: item.color || "#e0e8ff" }}>{item.val}</div>
                        {item.note && <div style={s.secNote}>{item.note}</div>}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Change Password */}
                <button
                  style={{ ...s.pwToggleBtn, marginTop: 16 }}
                  onClick={() => { setShowPwForm(v => !v); setPwStatus(null); }}>
                  🔐 {showPwForm ? "Cancel Password Change" : "Change Password"}
                </button>

                {showPwForm && (
                  <div style={s.pwBox}>
                    {pwStatus && (
                      <div style={{
                        ...s.alert, marginBottom: 14,
                        background: pwStatus.type === "success" ? "rgba(0,232,122,0.07)" : "rgba(255,60,87,0.08)",
                        border:     pwStatus.type === "success" ? "1px solid rgba(0,232,122,0.25)" : "1px solid rgba(255,60,87,0.3)",
                        color:      pwStatus.type === "success" ? "#00e87a" : "#ff6b8a",
                      }}>
                        {pwStatus.type === "success" ? "✓" : "⚠"} {pwStatus.msg}
                      </div>
                    )}
                    <Field label="Current Password" value={pwForm.currentPassword} type="password"
                      onChange={v => setPwForm(f => ({ ...f, currentPassword: v }))}
                      placeholder="Enter current password" />
                    <Field label="New Password" value={pwForm.newPassword} type="password"
                      onChange={v => setPwForm(f => ({ ...f, newPassword: v }))}
                      placeholder="Min 6 characters" />
                    <Field label="Confirm New Password" value={pwForm.confirmPassword} type="password"
                      onChange={v => setPwForm(f => ({ ...f, confirmPassword: v }))}
                      placeholder="Re-enter new password" />
                    <button
                      style={{ ...s.saveBtn, background: "linear-gradient(135deg,#00C2FF,#0080AA)", boxShadow: "0 4px 20px rgba(0,194,255,0.3)", opacity: pwLoading ? 0.6 : 1, cursor: pwLoading ? "not-allowed" : "pointer", marginBottom: 0 }}
                      onClick={handlePasswordChange}
                      disabled={pwLoading}>
                      {pwLoading ? <span style={s.spinner} /> : "🔐 Update Password"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, readOnly, hint, type = "text" }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={s.fieldLbl}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange ? e => onChange(e.target.value) : undefined}
        placeholder={placeholder || ""}
        readOnly={readOnly}
        style={{
          ...s.input,
          background: readOnly ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.4)",
          color: readOnly ? "rgba(160,180,220,0.4)" : "#e0e8ff",
          cursor: readOnly ? "not-allowed" : "text",
        }}
      />
      {hint && <div style={s.fieldHint}>{hint}</div>}
    </div>
  );
}

const fmtU = v => "USDT " + (Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const s = {
  page:      { minHeight: "100vh", background: "#060D1A", overflowX: "hidden", width: "100%" },
  container: { maxWidth: 1100, margin: "0 auto", padding: "32px 24px", boxSizing: "border-box", width: "100%", overflowX: "hidden" },
  header:    { marginBottom: 28 },
  title: {
    fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 26,
    background: `linear-gradient(135deg,#f0e0a0,${G},${GD})`,
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
    letterSpacing: 2, marginBottom: 6,
  },
  sub: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 14, color: "rgba(160,180,220,0.45)" },

  grid: { display: "grid", gridTemplateColumns: "320px 1fr", gap: 20, alignItems: "start" },
  leftCol:  { display: "flex", flexDirection: "column", gap: 16, minWidth: 0, overflow: "hidden" },
  rightCol: { display: "flex", flexDirection: "column", gap: 16, minWidth: 0, overflow: "hidden" },

  topLine: { height: 2, background: `linear-gradient(90deg,transparent,${G2},${G},${GD},transparent)` },

  avatarCard: {
    background: "rgba(8,18,40,0.95)", border: "1px solid rgba(255,184,0,0.12)",
    borderRadius: 16, overflow: "hidden", textAlign: "center",
    boxShadow: "0 8px 40px rgba(0,0,0,0.3)", width: "100%", boxSizing: "border-box",
  },
  avatarWrap: { position: "relative", display: "inline-block", margin: "28px auto 16px" },
  avatarRing: {
    width: 90, height: 90, borderRadius: "50%",
    background: GRAD, padding: 3,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  avatar: {
    width: "100%", height: "100%", borderRadius: "50%",
    background: "#0a1020",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: "'Figtree',sans-serif", fontWeight: 900, fontSize: 28, color: G2,
  },
  avatarGlow: {
    position: "absolute", inset: -8, borderRadius: "50%",
    background: "radial-gradient(circle,rgba(255,184,0,0.15),transparent 70%)",
    pointerEvents: "none",
  },
  uName: { fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 16, color: "#e0e8ff", marginBottom: 4 },
  uEmail: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 12, color: "rgba(160,180,220,0.45)", marginBottom: 14 },
  planBadge: {
    display: "inline-block", fontFamily: "'Figtree',sans-serif", fontWeight: 700,
    fontSize: 10, color: "#060D1A", borderRadius: 20, padding: "5px 16px",
    letterSpacing: 1.5, marginBottom: 10,
  },
  joinInfo: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 11, color: "rgba(160,180,220,0.35)", padding: "0 0 20px" },

  statsCard: {
    background: "rgba(8,18,40,0.95)", border: "1px solid rgba(255,184,0,0.1)",
    borderRadius: 14, overflow: "hidden", width: "100%", boxSizing: "border-box",
  },
  statRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 16px", gap: 8, minWidth: 0, width: "100%", boxSizing: "border-box" },
  statLbl: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 12, color: "rgba(160,180,220,0.45)", flexShrink: 0 },
  statVal: { fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 12, whiteSpace: "nowrap", flexShrink: 0 },

  refCard: {
    background: "rgba(8,18,40,0.95)", border: "1px solid rgba(255,184,0,0.1)",
    borderRadius: 14, overflow: "hidden", padding: "0 0 16px",
    width: "100%", boxSizing: "border-box",
  },
  refHead: { display: "flex", alignItems: "center", gap: 8, padding: "16px 18px 10px" },
  refIcon: { fontSize: 18, color: G },
  refTitle: { fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 12, color: G2, letterSpacing: 0.5 },
  refLinkBox: {
    margin: "0 16px 10px",
    background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,184,0,0.1)",
    borderRadius: 8, padding: "10px 12px",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 10,
    color: "rgba(160,180,220,0.5)",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    boxSizing: "border-box", maxWidth: "100%",
  },
  refLinkText: { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" },
  copyBtn: {
    display: "block", width: "calc(100% - 32px)", margin: "0 16px 10px",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 600, fontSize: 12,
    border: "1px solid", borderRadius: 8, padding: "10px",
    cursor: "pointer", transition: "all 0.2s", letterSpacing: 0.3,
  },
  shareRow: { display: "flex", gap: 8, padding: "0 16px" },
  shareBtn: {
    flex: 1, fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 600, fontSize: 11,
    border: "1px solid", borderRadius: 8, padding: "8px 4px",
    textDecoration: "none", textAlign: "center", letterSpacing: 0.3,
    transition: "opacity 0.2s",
  },

  formCard: {
    background: "rgba(8,18,40,0.95)", border: "1px solid rgba(255,184,0,0.1)",
    borderRadius: 16, overflow: "hidden", boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
  },
  formInner: { padding: "24px 28px" },
  formTitle: { fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 16, color: "#e0e8ff", letterSpacing: 1, marginBottom: 20 },

  alert: {
    padding: "12px 16px", borderRadius: 8, marginBottom: 18,
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 13,
    display: "flex", alignItems: "center", gap: 8,
  },
  fieldLbl: {
    display: "block", fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 600, fontSize: 11,
    color: "rgba(255,184,0,0.6)", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6,
  },
  input: {
    width: "100%", padding: "13px 14px",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 14, color: "#e0e8ff",
    border: "1px solid rgba(255,184,0,0.12)", borderRadius: 8,
    outline: "none", boxSizing: "border-box",
  },
  fieldHint: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 11, color: "rgba(160,180,220,0.3)", marginTop: 4 },
  saveBtn: {
    width: "100%", padding: "14px",
    fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 13,
    color: "#060D1A", background: GRAD,
    border: "none", borderRadius: 8, letterSpacing: 1,
    boxShadow: "0 4px 24px rgba(255,184,0,0.35)",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    marginBottom: 28,
  },
  spinner: {
    display: "inline-block", width: 18, height: 18,
    border: "2px solid rgba(0,0,0,0.3)", borderTop: "2px solid #060D1A",
    borderRadius: "50%", animation: "spin 0.7s linear infinite",
  },

  secTitle: {
    fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 13,
    color: "rgba(255,184,0,0.7)", letterSpacing: 1, marginBottom: 14,
    paddingTop: 4, borderTop: "1px solid rgba(255,184,0,0.07)",
    paddingTop: 20,
  },
  secGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  secItem: {
    display: "flex", alignItems: "flex-start", gap: 10,
    background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,184,0,0.07)",
    borderRadius: 10, padding: "12px 14px",
  },
  secIcon: { fontSize: 18, flexShrink: 0 },
  secLbl: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 10, color: "rgba(160,180,220,0.4)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 },
  secVal: { fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 12, marginBottom: 2 },
  secNote: { fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 10, color: "rgba(160,180,220,0.3)" },

  pwToggleBtn: {
    width: "100%", padding: "12px",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 600, fontSize: 13,
    color: "#00C2FF", background: "rgba(0,194,255,0.08)",
    border: "1px solid rgba(0,194,255,0.25)", borderRadius: 8,
    cursor: "pointer", letterSpacing: 0.3, transition: "all 0.2s",
  },
  pwBox: {
    marginTop: 14, padding: "18px",
    background: "rgba(0,194,255,0.04)",
    border: "1px solid rgba(0,194,255,0.12)",
    borderRadius: 10,
  },
};


